import { useState, useEffect } from "react";
import React from "react";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { useMediaQuery } from "@mui/material";
import {
  Link,
  Typography,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Avatar,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import IconButton from "@mui/material/IconButton";

import axios from "axios";

import { host } from "../constant/backendpath";
import { useLoader } from "../context/LoaderContext";

import Collapse from "@mui/material/Collapse";
import Chip from "@mui/material/Chip";

import ProjectDetails from "./ProjectDetails";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#4A90E2",
    color: "#FFFFFF",
    fontWeight: "bold",
    textAlign: "center",
    whiteSpace: "nowrap",
  },

  [`&.${tableCellClasses.body}`]: {
    fontSize: "0.8rem",
    textAlign: "center",
    padding: "8px",
  },
}));


const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: "#F1F5FA",
  },

  "&:nth-of-type(even)": {
    backgroundColor: "#E8EFF7",
  },

  "&:hover": {
    backgroundColor: "#D0E2F2",
  },

  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const ProjectTable = ({ old, profileDetails, setCurrent }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const { setLoading } = useLoader();
  const [projects, setProjects] = useState([]);
  const isMobile = useMediaQuery("(max-width:735px)");
  const [selectedProject, setSelectedProject] = useState(null);
  const availableYears = [2025, 2026];
  const [selectedYear, setSelectedYear] = useState(
    availableYears[availableYears.length - 1],
  );
  const [expandedRow, setExpandedRow] = useState(null);
  const handleRowClick = (index) => {
    setExpandedRow(expandedRow === index ? null : index);
  };

  const handleViewProject = (project) => {
    setSelectedProject(project);
  };

  async function fetchproject(year = selectedYear) {
    setLoading(true);

    try {
      const res = await axios.get(`${host}project?projectYear=${year}`, {
        withCredentials: true,
      });

      if (res.status === 200) {
        setProjects(res.data);
      }
    } catch (err) {
      console.error("Project fetch error:", err);
    } finally {
      setLoading(false);
    }
  }


  useEffect(() => {
    fetchproject(selectedYear);
  }, [selectedYear]);


  const filteredProjects = projects.filter((project) =>
    Object.values(project).some((value) => {
      if (value === null || value === undefined) {
        return false;
      }

      // Handle mentor objects
      if (typeof value === "object") {
        return Object.values(value).some((nestedValue) =>
          nestedValue
            ?.toString()
            .toLowerCase()
            .includes(searchTerm.toLowerCase()),
        );
      }

      return value.toString().toLowerCase().includes(searchTerm.toLowerCase());
    }),
  );


  if (selectedProject) {
    return (
      <ProjectDetails
        project={selectedProject}
        onBack={() => setSelectedProject(null)}
        fetch={fetchproject}
        profile={profileDetails}
      />
    );
  }


  return (
    <>
      <Typography
        sx={{
          fontWeight: "bold",
          fontSize: "1.5em",
          color: "black",
          textAlign: "center",
          marginTop: "1em",
        }}
      >
        Projects
      </Typography>


      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          flexDirection: isMobile ? "column" : "row",
          alignItems: "center",
          gap: 2,
          mt: "1em",
          mb: "1em",
        }}
      >

        <FormControl
          size="small"
          sx={{
            minWidth: 140,
          }}
        >
          <InputLabel id="project-year-select-label">Project Year</InputLabel>

          <Select
            labelId="project-year-select-label"
            value={selectedYear}
            label="Project Year"
            onChange={(e) => setSelectedYear(Number(e.target.value))}
          >
            {availableYears.map((year) => (
              <MenuItem key={year} value={year}>
                {year}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          variant="outlined"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{
            width: isMobile ? "75%" : "50%",
            backgroundColor: "#F1F5FA",
            borderRadius: "20px",
            color: "black",
            "& .MuiOutlinedInput-root": {
              borderRadius: "20px",
              "& fieldset": {
                borderColor: "transparent",
              },
              "&:hover fieldset": {
                borderColor: "transparent",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#1E88E5",
              },
            },
            input: {
              color: "black",
              padding: "10px",
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon
                  sx={{
                    color: "gray",
                  }}
                />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          boxShadow: 3,
          borderRadius: 2,
          width: "100%",
          maxWidth: "95%",
          margin: "auto",
          marginTop: "1em",
          mb: 6,
          overflowX: "auto",
        }}
      >
        <Table
          sx={{
            minWidth: 780,
            width: "100%",
          }}
          aria-label="customized table"
        >

          <TableHead>
            <TableRow>
              <StyledTableCell>S.no</StyledTableCell>
              <StyledTableCell>Project Title</StyledTableCell>
              <StyledTableCell>Team Leader Name</StyledTableCell>
              <StyledTableCell>Department</StyledTableCell>
              <StyledTableCell>Year</StyledTableCell>
              <StyledTableCell>
                {profileDetails.role === "mentor"
                  ? "Contact Number"
                  : "Register Number"}
              </StyledTableCell>
              <StyledTableCell>
                {old ? "More Details" : "Mentor"}
              </StyledTableCell>
              <StyledTableCell>Expand</StyledTableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredProjects.map((project, index) => (
              <React.Fragment key={project.teamId || index}>
                <StyledTableRow>
                  <StyledTableCell>{index + 1}</StyledTableCell>
                  <StyledTableCell>
                    <Typography
                      sx={{
                        fontWeight: 300,
                        fontSize: "0.82rem",
                      }}
                    >
                      {project.teamTitle || "-"}
                    </Typography>
                    {project.domain && (
                      <Chip
                        label={project.domain}
                        size="small"
                        sx={{
                          mt: 1,
                        }}
                      />
                    )}
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                      }}
                    >
                      {project.teamLeadName || "-"}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Chip
                      label={project.dept || "-"}
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: "0.7rem",
                        bgcolor: "#E3F2FD",
                        color: "#1565C0",
                      }}
                    />
                  </StyledTableCell>
                  <StyledTableCell>{project.year || "-"}</StyledTableCell>
                  <StyledTableCell>
                    {profileDetails.role === "mentor"
                      ? project.number || "-"
                      : project.reg_no || "-"}
                  </StyledTableCell>
                  <StyledTableCell>
                    {old ? (
                      <Link
                        href="#"
                        underline="hover"
                        sx={{
                          fontSize: "0.75rem",
                        }}
                      >
                        Read More
                      </Link>
                    ) : (
                      project.mentor1?.name || "Not Assigned"
                    )}
                  </StyledTableCell>

                  <StyledTableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleRowClick(index)}
                    >
                      {expandedRow === index ? (
                        <KeyboardArrowDownIcon />
                      ) : (
                        <KeyboardArrowRightIcon />
                      )}
                    </IconButton>
                  </StyledTableCell>
                </StyledTableRow>

                <TableRow>
                  <TableCell
                    colSpan={8}
                    sx={{
                      padding: 0,
                      border: 0,
                    }}
                  >
                    <Collapse
                      in={expandedRow === index}
                      timeout="auto"
                      unmountOnExit
                    >
                      <Box
                        sx={{
                          p: {
                            xs: 2,
                            sm: 3,
                          },

                          bgcolor: "#D0E2F2",

                          borderTop: "1px solid #D0E2F2",
                        }}
                      >

                        <Box
                          sx={{
                            display: "grid",

                            gridTemplateColumns: {
                              xs: "1fr",
                              lg: "2fr 1fr",
                            },

                            gap: 3,

                            width: "100%",
                          }}
                        >

                          <Box>
                            <Typography
                              variant="subtitle2"
                              fontWeight={600}
                              mb={2}
                            >
                              Team Members
                            </Typography>

                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns: {
                                  xs: "1fr",
                                  sm: "repeat(2, 1fr)",
                                  lg: "repeat(4, 1fr)",
                                },
                                gap: 1.5,
                              }}
                            >
                              {[
                                {
                                  name: project.teamLeadName,
                                  reg: project.reg_no,
                                  dept: project.dept,
                                  number: project.number,
                                  role: "Team Leader",
                                },
                                {
                                  name: project.member1Name,
                                  reg: project.member1Reg_no,
                                  dept: project.member1Dept,
                                  number: project.member1Number,
                                  role: "Member 1",
                                },
                                {
                                  name: project.member2Name,
                                  reg: project.member2Reg_no,
                                  dept: project.member2Dept,
                                  number: project.member2Number,
                                  role: "Member 2",
                                },
                                {
                                  name: project.member3Name,
                                  reg: project.member3Reg_no,
                                  dept: project.member3Dept,
                                  number: project.member3Number,
                                  role: "Member 3",
                                },
                              ]
                                .filter((member) => member.name)
                                .map((member, idx) => (
                                  <Paper
                                    key={idx}
                                    elevation={1}
                                    sx={{
                                      p: 1.5,
                                      borderRadius: 2,
                                      border: "1px solid #D0E2F2",
                                      backgroundColor: "#ffffff",
                                      minWidth: 0,
                                    }}
                                  >
                                    <Avatar
                                      sx={{
                                        width: 36,
                                        height: 36,
                                        fontSize: "0.75rem",
                                        bgcolor: "#1976d2",
                                        mb: 1,
                                      }}
                                    >
                                      {member.name?.charAt(0)}
                                    </Avatar>

                                    <Typography
                                      fontSize="0.65rem"
                                      color="primary"
                                      fontWeight={600}
                                    >
                                      {member.role}
                                    </Typography>

                                    <Typography
                                      fontSize="0.75rem"
                                      fontWeight={600}
                                      noWrap
                                    >
                                      {member.name}
                                    </Typography>

                                    <Typography
                                      fontSize="0.68rem"
                                      color="text.secondary"
                                      noWrap
                                    >
                                      {member.dept || "-"}
                                    </Typography>

                                    <Typography
                                      fontSize="0.68rem"
                                      color="text.secondary"
                                      noWrap
                                    >
                                      {profileDetails.role === "mentor"
                                        ? `Phone: ${member.number || "-"}`
                                        : `Reg: ${member.reg || "-"}`}
                                    </Typography>
                                  </Paper>
                                ))}
                            </Box>
                          </Box>
                          <Box>
                            <Typography
                              variant="subtitle2"
                              fontWeight={600}
                              mb={2}
                            >
                              Mentors
                            </Typography>

                            <Box
                              sx={{
                                display: "grid",

                                gridTemplateColumns: {
                                  xs: "1fr",
                                  sm: "repeat(2, 1fr)",
                                  lg: "repeat(2, 1fr)",
                                },
                                gap: 1.5,
                              }}
                            >
                              {project.mentor1 && (
                                <Paper
                                  elevation={1}
                                  sx={{
                                    p: 1.5,
                                    borderRadius: 2,
                                    border: "1px solid #D0E2F2",
                                    backgroundColor: "#ffffff",
                                    minWidth: 0,
                                  }}
                                >
                                  <Avatar
                                    sx={{
                                      width: 36,
                                      height: 36,
                                      fontSize: "0.75rem",
                                      bgcolor: "#43a047",
                                      mb: 1,
                                    }}
                                  >
                                    {project.mentor1.name?.charAt(0)}
                                  </Avatar>

                                  <Typography
                                    fontSize="0.65rem"
                                    color="success.main"
                                    fontWeight={600}
                                  >
                                    Mentor 1
                                  </Typography>

                                  <Typography
                                    fontSize="0.75rem"
                                    fontWeight={600}
                                    noWrap
                                  >
                                    {project.mentor1.name}
                                  </Typography>

                                  <Typography
                                    fontSize="0.68rem"
                                    color="text.secondary"
                                    noWrap
                                  >
                                    {project.mentor1.dept}
                                  </Typography>

                                  <Typography
                                    fontSize="0.68rem"
                                    color="text.secondary"
                                    noWrap
                                  >
                                    {project.mentor1.number}
                                  </Typography>
                                </Paper>
                              )}

                              {project.mentor2 && (
                                <Paper
                                  elevation={1}
                                  sx={{
                                    p: 1.5,
                                    borderRadius: 2,
                                    border: "1px solid #D0E2F2",
                                    backgroundColor: "#ffffff",
                                    minWidth: 0,
                                  }}
                                >
                                  <Avatar
                                    sx={{
                                      width: 36,
                                      height: 36,
                                      fontSize: "0.75rem",
                                      bgcolor: "#43a047",
                                      mb: 1,
                                    }}
                                  >
                                    {project.mentor2.name?.charAt(0)}
                                  </Avatar>

                                  <Typography
                                    fontSize="0.65rem"
                                    color="success.main"
                                    fontWeight={600}
                                  >
                                    Mentor 2
                                  </Typography>

                                  <Typography
                                    fontSize="0.75rem"
                                    fontWeight={600}
                                    noWrap
                                  >
                                    {project.mentor2.name}
                                  </Typography>

                                  <Typography
                                    fontSize="0.68rem"
                                    color="text.secondary"
                                    noWrap
                                  >
                                    {project.mentor2.dept}
                                  </Typography>

                                  <Typography
                                    fontSize="0.68rem"
                                    color="text.secondary"
                                    noWrap
                                  >
                                    {project.mentor2.number}
                                  </Typography>
                                </Paper>
                              )}
                            </Box>

                            {!project.mentor1 && !project.mentor2 && (
                              <Typography
                                fontSize="0.75rem"
                                color="text.secondary"
                                sx={{
                                  mt: 1,
                                }}
                              >
                                No mentors assigned
                              </Typography>
                            )}
                          </Box>
                        </Box>

                        {(profileDetails.role === "mentor" ||
                          profileDetails.role === "admin") && (
                          <Box
                            sx={{
                              display: "flex",

                              justifyContent: "flex-end",

                              mt: 3,
                            }}
                          >
                            <Typography
                              sx={{
                                cursor: "pointer",
                                color: "#1976d2",
                                fontWeight: 600,
                                fontSize: "0.85rem",
                              }}
                              onClick={() => handleViewProject(project)}
                            >
                              View Project
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
            {filteredProjects.length === 0 && (
              <TableRow>
                <StyledTableCell
                  colSpan={8}
                  sx={{
                    py: 5,
                    color: "text.secondary",
                  }}
                >
                  No projects found
                </StyledTableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default ProjectTable;
